import React, { useState, useEffect } from 'react';

const RealTimeInsightPreview: React.FC = () => {
  const [activeUpdate, setActiveUpdate] = useState(0);
  const [pulseStudent, setPulseStudent] = useState(false);
  const [pulseTeacher, setPulseTeacher] = useState(false);
  const [pulseParent, setPulseParent] = useState(false);

  const updates = [
    { type: 'correct', subject: 'Maths', topic: 'Fractions', change: '+8%', xp: '+25' },
    { type: 'incorrect', subject: 'English', topic: 'Grammar', change: '-3%', xp: '0' },
    { type: 'correct', subject: 'Maths', topic: 'Decimals', change: '+5%', xp: '+25' },
    { type: 'mastery', subject: 'Maths', topic: 'Addition', change: '100%', xp: '+100' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPulseStudent(true);

      setTimeout(() => {
        setPulseTeacher(true);
        setPulseParent(true);
      }, 400);

      setTimeout(() => {
        setActiveUpdate(prev => (prev + 1) % updates.length);
        setPulseStudent(false);
        setPulseTeacher(false);
        setPulseParent(false);
      }, 1500);
    }, 2500);

    return () => clearInterval(interval);
  }, []);

  const current = updates[activeUpdate];

  return (
    <div
      className="w-full rounded-xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          Real-Time Updates
        </span>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#10b981' }} />
          <span className="text-[10px]" style={{ color: '#10b981' }}>LIVE</span>
        </div>
      </div>

      {/* Main visualization - fixed height container */}
      <div className="p-4" style={{ minHeight: '280px' }}>
        {/* Central event */}
        <div className="flex justify-center mb-2">
          <div
            className="px-4 py-2 rounded-lg"
            style={{
              background: current.type === 'correct'
                ? 'rgba(16, 185, 129, 0.2)'
                : current.type === 'mastery'
                  ? 'rgba(245, 158, 11, 0.2)'
                  : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${
                current.type === 'correct'
                  ? 'rgba(16, 185, 129, 0.4)'
                  : current.type === 'mastery'
                    ? 'rgba(245, 158, 11, 0.4)'
                    : 'rgba(239, 68, 68, 0.4)'
              }`,
            }}
          >
            <div className="flex items-center gap-3">
              <span className="text-lg">
                {current.type === 'correct' ? '✓' : current.type === 'mastery' ? '⭐' : '✗'}
              </span>
              <div>
                <div className="text-xs font-medium text-white">
                  {current.subject} • {current.topic}
                </div>
                <div className="text-[10px]" style={{ color: '#64748b' }}>
                  {current.type === 'mastery' ? 'Mastery Achieved!' : 'Question answered'}
                </div>
              </div>
              <div className="text-right">
                <div
                  className="text-sm font-bold"
                  style={{
                    color: current.type === 'incorrect' ? '#ef4444' : '#10b981',
                  }}
                >
                  {current.change}
                </div>
                {current.xp !== '0' && (
                  <div className="text-[10px]" style={{ color: '#f59e0b' }}>
                    {current.xp} XP
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Single line down from event */}
        <div className="flex justify-center">
          <div
            className="w-0.5 h-4 transition-all duration-300"
            style={{
              background: (pulseStudent || pulseTeacher || pulseParent)
                ? '#8b5cf6'
                : 'rgba(255, 255, 255, 0.1)'
            }}
          />
        </div>

        {/* Connection structure: horizontal bar with vertical drops aligned to stakeholders */}
        <div className="relative mx-8" style={{ height: '120px' }}>
          {/* Horizontal bar - inset to align with vertical drop center points */}
          <div
            className="absolute h-0.5 transition-all duration-300"
            style={{
              top: '0px',
              left: '29px',
              right: '29px',
              background: (pulseStudent || pulseTeacher || pulseParent)
                ? 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)'
                : 'rgba(255, 255, 255, 0.1)',
            }}
          />

          {/* Three columns with vertical lines and stakeholders */}
          <div className="flex justify-between h-full">
            {/* Student column */}
            <div className="flex flex-col items-center">
              {/* Vertical drop */}
              <div
                className="w-0.5 h-5 transition-all duration-300"
                style={{
                  background: pulseStudent
                    ? '#3b82f6'
                    : 'rgba(255, 255, 255, 0.1)'
                }}
              />
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  pulseStudent ? 'scale-110' : ''
                }`}
                style={{
                  background: pulseStudent ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.15)',
                  border: pulseStudent ? '2px solid #3b82f6' : '2px solid rgba(59, 130, 246, 0.4)',
                  boxShadow: pulseStudent ? '0 0 20px rgba(59, 130, 246, 0.4)' : 'none',
                }}
              >
                <span className="text-xl">🎓</span>
              </div>
              <span className="text-[10px] mt-1 font-medium" style={{ color: pulseStudent ? '#3b82f6' : '#64748b' }}>
                Student
              </span>
              <span
                className="text-[9px] transition-opacity duration-300"
                style={{ color: '#3b82f6', opacity: pulseStudent ? 1 : 0, height: '14px' }}
              >
                Sees progress
              </span>
            </div>

            {/* Teacher column */}
            <div className="flex flex-col items-center">
              {/* Vertical drop */}
              <div
                className="w-0.5 h-5 transition-all duration-300"
                style={{
                  background: pulseTeacher
                    ? '#10b981'
                    : 'rgba(255, 255, 255, 0.1)'
                }}
              />
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  pulseTeacher ? 'scale-110' : ''
                }`}
                style={{
                  background: pulseTeacher ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.15)',
                  border: pulseTeacher ? '2px solid #10b981' : '2px solid rgba(16, 185, 129, 0.4)',
                  boxShadow: pulseTeacher ? '0 0 20px rgba(16, 185, 129, 0.4)' : 'none',
                }}
              >
                <span className="text-xl">👩‍🏫</span>
              </div>
              <span className="text-[10px] mt-1 font-medium" style={{ color: pulseTeacher ? '#10b981' : '#64748b' }}>
                Teacher
              </span>
              <span
                className="text-[9px] transition-opacity duration-300"
                style={{ color: '#10b981', opacity: pulseTeacher ? 1 : 0, height: '14px' }}
              >
                Gets insight
              </span>
            </div>

            {/* Parent column */}
            <div className="flex flex-col items-center">
              {/* Vertical drop */}
              <div
                className="w-0.5 h-5 transition-all duration-300"
                style={{
                  background: pulseParent
                    ? '#ec4899'
                    : 'rgba(255, 255, 255, 0.1)'
                }}
              />
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  pulseParent ? 'scale-110' : ''
                }`}
                style={{
                  background: pulseParent ? 'rgba(236, 72, 153, 0.3)' : 'rgba(236, 72, 153, 0.15)',
                  border: pulseParent ? '2px solid #ec4899' : '2px solid rgba(236, 72, 153, 0.4)',
                  boxShadow: pulseParent ? '0 0 20px rgba(236, 72, 153, 0.4)' : 'none',
                }}
              >
                <span className="text-xl">👨‍👩‍👧</span>
              </div>
              <span className="text-[10px] mt-1 font-medium" style={{ color: pulseParent ? '#ec4899' : '#64748b' }}>
                Parent
              </span>
              <span
                className="text-[9px] transition-opacity duration-300"
                style={{ color: '#ec4899', opacity: pulseParent ? 1 : 0, height: '14px' }}
              >
                Stays informed
              </span>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div
          className="mt-4 p-3 rounded-lg flex items-center justify-between"
          style={{ background: 'rgba(255, 255, 255, 0.03)' }}
        >
          <div className="text-center flex-1">
            <div className="text-lg font-bold text-white">0s</div>
            <div className="text-[9px]" style={{ color: '#64748b' }}>Delay</div>
          </div>
          <div className="h-8 w-px" style={{ background: 'rgba(255, 255, 255, 0.1)' }} />
          <div className="text-center flex-1">
            <div className="text-lg font-bold text-white">3</div>
            <div className="text-[9px]" style={{ color: '#64748b' }}>Stakeholders</div>
          </div>
          <div className="h-8 w-px" style={{ background: 'rgba(255, 255, 255, 0.1)' }} />
          <div className="text-center flex-1">
            <div className="text-lg font-bold text-white">∞</div>
            <div className="text-[9px]" style={{ color: '#64748b' }}>Updates</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 text-center"
        style={{ background: 'rgba(255, 255, 255, 0.02)', borderTop: '1px solid rgba(255, 255, 255, 0.05)' }}
      >
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          Every answer instantly updates all dashboards
        </span>
      </div>
    </div>
  );
};

export default RealTimeInsightPreview;
